import Service, { service } from '@ember/service';
import { cancel, later } from '@ember/runloop';
import { queryManager } from 'ember-apollo-client';
import reviewableTicketsQuery from 'my-phorest/gql/queries/reviewable-tickets.graphql';
import { registerDestructor } from '@ember/destroyable';
import { restartableTask } from 'ember-concurrency';
import config from 'my-phorest/config/environment';
import { action } from '@ember/object';

const REVIEWABLE_TICKETS_EVENTS = ['branchChange', 'userChange'];

export default class ReviewableTicketsService extends Service {
  @service eventPropagator;
  @service session;
  @service errorHandler;
  @queryManager apollo;

  REVIEWABLE_TICKETS_POLLING_INTERVAL = 3600000; // 1 hour
  _pollingInterval = null;
  _pollingActive = true;

  constructor() {
    super(...arguments);

    // disabling polling in test environment
    // to avoid unnecessary polling during tests
    if (config.environment === 'test') {
      this._pollingActive = false;
    }

    this.boundFetchTickets();

    REVIEWABLE_TICKETS_EVENTS.forEach((event) => {
      this.eventPropagator.subscribe(event, this.boundFetchTickets);
    });

    this.boundFetchTickets();
    this._scheduleFetchTickets();

    registerDestructor(this, () => {
      REVIEWABLE_TICKETS_EVENTS.forEach((event) => {
        this.eventPropagator.unsubscribe(event, this.boundFetchTickets);
      });

      this.cancelPolling();
    });
  }

  @action
  boundFetchTickets() {
    this.fetchReviewableTicketsTask.perform();
  }

  _scheduleFetchTickets() {
    if (!this._pollingActive) {
      return;
    }

    this._pollingInterval = later(
      this,
      () => {
        this.boundFetchTickets();

        if (this._pollingActive) {
          this._scheduleFetchTickets();
        }
      },
      this.REVIEWABLE_TICKETS_POLLING_INTERVAL
    );
  }

  get reviewableTicketsCount() {
    return (
      this.fetchReviewableTicketsTask.lastSuccessful?.value?.totalResults || 0
    );
  }

  cancelPolling() {
    this._pollingActive = false;

    if (this._pollingInterval) {
      cancel(this._pollingInterval);
      this._pollingInterval = null;
    }
  }

  @restartableTask
  *fetchReviewableTicketsTask() {
    if (
      !this.session.currentAccountId ||
      !this.session.user?.email ||
      this.session.isGlobalUser
    ) {
      return;
    }

    try {
      return yield this.apollo.query(
        {
          query: reviewableTicketsQuery,
          fetchPolicy: 'no-cache',
          variables: {
            accountId: this.session.currentAccountId,
            userEmail: this.session.user.email,
          },
        },
        'reviewableTickets'
      );
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }
}
